import { useCallback, useMemo } from "react";
import { matchRoutes, useLocation } from "react-router-dom";
import { getPrivateRoutes, NewAppRoute } from "../../../../../AppRoutes";
import { SupportResource } from "./resources";

export type SupportContext = {
  url: string;
  route?: string;
};

const flattenPaths = (routes: NewAppRoute[]): { path: string }[] =>
  routes.flatMap(route => [{ path: route.path }, ...flattenPaths(route.children ?? [])]);

type Params = Record<string, string | undefined>;

// Most specific resource per route; the step tags already carry their parent id
const ROUTE_RESOURCES: Record<string, (p: Params) => SupportResource | undefined> = {
  "/flows/edit/:workflowId": ({ workflowId }) =>
    workflowId ? { type: "workflow", id: workflowId } : undefined,
  "/flows/edit/:workflowId/:stepId": ({ workflowId, stepId }) =>
    workflowId && stepId ? { type: "workflowStep", workflowId, id: stepId } : undefined,
  "/triggers/:triggerId": ({ triggerId }) =>
    triggerId ? { type: "trigger", id: triggerId } : undefined,
  "/employees/:employeeId": ({ employeeId }) =>
    employeeId ? { type: "employee", id: employeeId } : undefined,
  "/employees/:employeeId/:section": ({ employeeId }) =>
    employeeId ? { type: "employee", id: employeeId } : undefined,
  "/forms/:id": ({ id }) => (id ? { type: "form", id } : undefined),
  "/forms/:id/:fieldId": ({ id }) => (id ? { type: "form", id } : undefined),
  "/forms/:id/responses/:responseId": ({ id }) => (id ? { type: "form", id } : undefined),
  "/automations/:automationId": ({ automationId }) =>
    automationId ? { type: "automation", id: automationId } : undefined,
  "/automations/:automationId/steps/:stepId": ({ automationId, stepId }) =>
    automationId && stepId ? { type: "automationStep", automationId, id: stepId } : undefined
};

export const useSupportContext = () => {
  const location = useLocation();
  const routes = useMemo(() => flattenPaths(getPrivateRoutes()), []);

  const match = useMemo(() => {
    const matches = matchRoutes(routes, location);
    return matches ? matches[matches.length - 1] : undefined;
  }, [routes, location]);

  const capture = useCallback((): SupportContext => {
    if (!match) return { url: window.location.href };
    return { url: window.location.href, route: match.route.path };
  }, [match]);

  const autoResource = useMemo(
    () => (match ? ROUTE_RESOURCES[match.route.path]?.(match.params) : undefined),
    [match]
  );

  return { capture, autoResource };
};
